import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { fetchAdminReportCount } from "../../features/admin/slice-extensions/admin-moderation-slice-extension";
import { ROUTES } from "../../routes";

interface AdminTabsProps {
  /** Bump to re-read the open-report count after the Reports page handled one. */
  reportCountVersion?: number;
}

/** Sub-navigation between the admin back-office pages (Users | Feedback | Reports | Referrals | Acquisition). */
const AdminTabs: React.FC<AdminTabsProps> = ({ reportCountVersion = 0 }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [openReports, setOpenReports] = useState(0);

  useEffect(() => {
    dispatch(fetchAdminReportCount())
      .unwrap()
      .then(setOpenReports)
      .catch(() => setOpenReports(0));
  }, [dispatch, reportCountVersion]);

  const tabs = [
    { route: ROUTES.ADMIN_USERS, label: t("admin.tabs.users"), count: 0 },
    { route: ROUTES.ADMIN_FEEDBACK, label: t("admin.tabs.feedback"), count: 0 },
    { route: ROUTES.ADMIN_REPORTS, label: t("admin.tabs.reports"), count: openReports },
    { route: ROUTES.ADMIN_REFERRALS, label: t("admin.tabs.referrals"), count: 0 },
    { route: ROUTES.ADMIN_ACQUISITION, label: t("admin.tabs.acquisition"), count: 0 },
  ];

  return (
    <nav className="flex flex-wrap gap-2" aria-label={t("admin.title")}>
      {tabs.map((tab) => {
        const isActive = location.pathname === tab.route;
        return (
          <Link
            key={tab.route}
            to={tab.route}
            aria-current={isActive ? "page" : undefined}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${
              isActive
                ? "bg-primary-95 text-primary-40 dark:bg-primary-30 dark:text-primary-90"
                : "text-grey-40 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="ml-1.5 font-semibold" data-testid="admin-reports-badge">
                {tab.count}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
};

export default AdminTabs;
